import {inject, injectable} from 'inversify';

import type {CoreWaitlistItemApiModel} from '../../../../api/types/core/models';
import type {IApiDataMapper} from '../../../ports/types';

import {CouponMapperJrpc} from '../../Coupon/mappers/Coupon.mapper.jrpc';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {WaitlistItemFactory} from '../WaitlistItem.factory';
import {WaitlistItemModel} from '../WaitlistItem.model';
import {WaitlistItemExpandJrpc} from './WaitlistItem.expand.jrpc';

@injectable()
export class WaitlistItemMapperJrpc
  implements
    IApiDataMapper<
      WaitlistItemMapperJrpc.ApiData,
      WaitlistItemModel.Props,
      WaitlistItemModel
    >
{
  constructor(
    @inject(WaitlistItemFactory)
    private readonly factory: WaitlistItemFactory,
    @inject(WaitlistItemExpandJrpc)
    public readonly expand: WaitlistItemExpandJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(SubtenantMapperJrpc)
    private readonly subtenantMapper: SubtenantMapperJrpc,
    @inject(CouponMapperJrpc)
    private readonly couponMapper: CouponMapperJrpc,
  ) {}

  mapDataToModel(
    apiData: WaitlistItemMapperJrpc.ExpandedApiData,
  ): WaitlistItemModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: WaitlistItemMapperJrpc.ExpandedApiData,
  ): WaitlistItemModel.Props {
    return {
      coupon: this.coupon(apiData),
      createdDate: this.dateTimeMapper.mapToModel(apiData.created),
      email: '',
      id: apiData._id,
      invitedDate: this.dateTimeMapper.mapNullableToModel(apiData.invited_on),
      joinedDate: this.dateTimeMapper.mapNullableToModel(apiData.joined_on),
      position: apiData.position,
      subtenant: this.subtenantMapper.mapExpandedToModelOrEmpty(
        apiData.subtenant,
      ),
      updateDate: this.dateTimeMapper.mapToModel(apiData.updated),
      user: this.userMapper.mapDataToModelOrEmpty(apiData.user),
    };
  }

  private coupon(
    apiData: WaitlistItemMapperJrpc.ExpandedApiData,
  ): WaitlistItemModel.Props['coupon'] {
    return apiData.coupon
      ? this.couponMapper.mapDataToModel(apiData.coupon)
      : null;
  }
}

export namespace WaitlistItemMapperJrpc {
  export type ApiData = CoreWaitlistItemApiModel;

  export type ExpandedApiData = ApiData & WaitlistItemExpandJrpc.ExpandedFields;
}
